import { z } from 'zod';

export const ACCEPTED_RECEIPT_TYPES = [
  'image/jpeg',
  'image/png',
  'application/pdf',
] as const;

export const MAX_RECEIPT_SIZE = 8 * 1024 * 1024; // 8 MB
export const MAX_NOTES_LENGTH = 500;
export const MAX_TRANSFER_AGE_DAYS = 90;

/**
 * Construye el schema del formulario de cancelación de deuda.
 * `maxAmount` es el saldo negativo actual: no se puede transferir más de lo que se debe.
 */
export const makeDebtPaymentSchema = (maxAmount: number) =>
  z.object({
    amount: z
      .number({ invalid_type_error: 'Ingresá un monto válido' })
      .positive('El monto debe ser mayor a cero')
      .refine(
        (value) => maxAmount <= 0 || value <= maxAmount,
        'No podés transferir más que tu saldo negativo actual',
      ),
    transferDate: z
      .date({
        required_error: 'Ingresá la fecha de la transferencia',
        invalid_type_error: 'Ingresá la fecha de la transferencia',
      })
      .refine((date) => {
        const endOfToday = new Date();
        endOfToday.setHours(23, 59, 59, 999);
        return date.getTime() <= endOfToday.getTime();
      }, 'La fecha no puede ser futura')
      .refine((date) => {
        const limit = new Date();
        limit.setDate(limit.getDate() - MAX_TRANSFER_AGE_DAYS);
        limit.setHours(0, 0, 0, 0);
        return date.getTime() >= limit.getTime();
      }, `La fecha no puede ser anterior a ${MAX_TRANSFER_AGE_DAYS} días`),
    receipt: z
      .instanceof(File, { message: 'Adjuntá el comprobante' })
      .refine(
        (file) => (ACCEPTED_RECEIPT_TYPES as readonly string[]).includes(file.type),
        'El archivo debe ser JPG, PNG o PDF',
      )
      .refine(
        (file) => file.size <= MAX_RECEIPT_SIZE,
        'El comprobante no puede superar los 8 MB',
      ),
    notes: z
      .string()
      .max(MAX_NOTES_LENGTH, `Máximo ${MAX_NOTES_LENGTH} caracteres`)
      .optional(),
  });

export type DebtPaymentFormValues = z.infer<
  ReturnType<typeof makeDebtPaymentSchema>
>;
